import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  Cancel,
  CheckCircle,
  HighlightOff,
  LocalShipping,
  Handshake,
  Assignment,
  Receipt,
  Payments,
  History,
  EditNote,
  Business,
  ReceiptLong,
  Layers,
} from '@mui/icons-material';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { procurementService, AmendPoInput } from '../../services/procurementService';
import assetService, { AssetCategory } from '../../services/assetService';

// ---------------------------------------------------------------------------
// Types -- mirror the raw shape returned by
// GET /procurement/purchase-orders/:id (erp-api src/models/Procurement.js
// getPurchaseOrderById): `data.purchaseOrder` is the po.* row plus vendor /
// cost-center / project / approver joins, with items / approval / related
// nested directly ON that same object (NOT sibling keys of the envelope).
// ---------------------------------------------------------------------------

type PoStatus =
  | 'DRAFT'
  | 'APPROVED'
  | 'SENT'
  | 'ACKNOWLEDGED'
  | 'PARTIALLY_RECEIVED'
  | 'FULLY_RECEIVED'
  | 'CLOSED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'PENDING'
  | 'DELIVERED';

type PoApprovalStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';

interface PurchaseOrderItem {
  id: number;
  product_id: string;
  product_name?: string;
  item_type?: string | null;
  line_description?: string | null;
  line_uom?: string | null;
  line_hsn_code?: string | null;
  hsn_code?: string | null;
  unit?: string | null;
  quantity: number | string;
  unit_price: number | string;
  discount_percent?: number | string;
  gst_rate?: number | string;
  tax_amount?: number | string;
  line_total: number | string;
  received_quantity?: number | string;
  invoiced_quantity?: number | string;
}

interface ApprovalRequest {
  id: number;
  entity_type: string;
  entity_id: number;
  title: string;
  amount: number | string | null;
  status: string;
  current_step: number;
  requested_by: number;
  completed_at: string | null;
  created_at: string;
}

interface ApprovalAction {
  id: number;
  request_id: number;
  step_number: number;
  approver_id: number;
  action: 'APPROVED' | 'REJECTED' | 'DELEGATED' | 'COMMENTED';
  comments: string | null;
  created_at: string;
}

interface RelatedRequisition {
  id: number;
  requisition_number: string;
  status: string;
  [key: string]: any;
}

interface RelatedGoodsReceipt {
  id: number;
  grn_number: string;
  receipt_date: string;
  status: string;
  warehouse_name?: string | null;
}

interface RelatedInvoice {
  id: number;
  invoice_no: string;
  invoice_date: string;
  net_amount: number | string;
  status: string;
  payment_status?: string;
}

interface RelatedPayment {
  id: number;
  payment_number: string;
  payment_date: string;
  net_amount: number | string;
  status: string;
}

interface PoAmendmentRow {
  id: number;
  version: number;
  reason: string;
  changed_by_name: string;
  changed_at: string;
  snapshot_json?: any;
}

interface PurchaseOrderDetail {
  id: number;
  po_number: string;
  vendor_id: string;
  vendor_name?: string;
  contact_person?: string;
  email?: string;
  vendor_gstin?: string;
  vendor_pan?: string;
  po_type: string;
  priority: string;
  status: PoStatus;
  approval_status: PoApprovalStatus;
  cost_center_name?: string | null;
  project_name?: string | null;
  department?: string | null;
  po_date: string;
  delivery_date?: string | null;
  expected_delivery_date?: string | null;
  payment_terms?: string | null;
  subtotal: number | string;
  tax_amount: number | string;
  total_amount: number | string;
  terms_conditions?: string | null;
  approved_by_name?: string | null;
  created_by_name?: string | null;
  created_at: string;
  items: PurchaseOrderItem[];
  approval: { request: ApprovalRequest; actions: ApprovalAction[] } | null;
  related: {
    requisition: RelatedRequisition | null;
    goodsReceipts: RelatedGoodsReceipt[];
    invoices: RelatedInvoice[];
    payments: RelatedPayment[];
  };
  amendments?: PoAmendmentRow[];
}

const STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: 'neutral',
  APPROVED: 'success',
  SENT: 'info',
  ACKNOWLEDGED: 'info',
  PARTIALLY_RECEIVED: 'warning',
  FULLY_RECEIVED: 'success',
  CLOSED: 'neutral',
  REJECTED: 'error',
  CANCELLED: 'neutral',
  PENDING: 'neutral',
  DELIVERED: 'success',
};

const APPROVAL_STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

const GRN_STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: 'neutral',
  RECEIVED: 'info',
  QC_PENDING: 'warning',
  ACCEPTED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
};

const PRIORITY_COLOR: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  LOW: 'default',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'error',
};

const TYPE_TINT: Record<string, string> = {
  MATERIAL: '#0284c7',
  SERVICE: '#7c3aed',
  CAPEX: '#dc2626',
  SUBCONTRACTING: '#d97706',
  JOB_WORK: '#059669',
  CONSUMABLE: '#64748b',
  ASSET: '#0f766e',
  OTHER: '#475569',
};

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');
const fmtDateTime = (v: string | null | undefined) => (v ? new Date(v).toLocaleString('en-IN') : '—');

const ACTION_LABEL: Record<string, string> = {
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  DELEGATED: 'Delegated',
  COMMENTED: 'Commented',
};

const ACTION_TONE: Record<string, StatusTone> = {
  APPROVED: 'success',
  REJECTED: 'error',
  DELEGATED: 'info',
  COMMENTED: 'neutral',
};

const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [po, setPo] = useState<PurchaseOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Amend dialog
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendReason, setAmendReason] = useState('');
  const [amendPriority, setAmendPriority] = useState('');
  const [amendExpectedDeliveryDate, setAmendExpectedDeliveryDate] = useState('');
  const [amendPaymentTerms, setAmendPaymentTerms] = useState('');
  const [amendNotes, setAmendNotes] = useState('');
  const [amendItems, setAmendItems] = useState<
    { id: number; description: string; quantity: string; unitPrice: string; original: PurchaseOrderItem }[]
  >([]);
  const [amendmentsFallback, setAmendmentsFallback] = useState<PoAmendmentRow[] | null>(null);

  // Capitalize dialog
  const [capitalizeOpen, setCapitalizeOpen] = useState(false);
  const [assetCategories, setAssetCategories] = useState<AssetCategory[]>([]);
  const [assetName, setAssetName] = useState('');
  const [assetCategoryId, setAssetCategoryId] = useState('');
  const [capitalizationDate, setCapitalizationDate] = useState(new Date().toISOString().slice(0, 10));
  const [capitalizeResult, setCapitalizeResult] = useState<{
    asset: any;
    journalEntry: any;
    capitalisedAmount: number | string;
  } | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { purchaseOrder } = await api.get<{ purchaseOrder: PurchaseOrderDetail }>(
        `/procurement/purchase-orders/${id}`
      );
      setPo(purchaseOrder);
    } catch (err: any) {
      setError(err.message || 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // The PO payload is expected to already carry `amendments`; fall back to the
  // dedicated endpoint only if it doesn't (older backend, or omitted).
  useEffect(() => {
    if (!id || !po || po.amendments !== undefined) return;
    procurementService
      .getPoAmendments(id)
      .then((r) => setAmendmentsFallback(r.amendments || []))
      .catch(() => setAmendmentsFallback([]));
  }, [id, po]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !po) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement')} sx={{ mb: 2 }}>
          Back to Procurement
        </Button>
        <Alert severity="error">{error || 'Purchase order not found'}</Alert>
      </Box>
    );
  }

  const canSubmit = po.status === 'DRAFT' && po.approval_status === 'DRAFT';
  const canApproveReject = po.approval_status === 'PENDING_APPROVAL';
  const canSend = po.status === 'APPROVED';
  const canAcknowledge = po.status === 'SENT';
  const canCancel = !(['FULLY_RECEIVED', 'CLOSED', 'CANCELLED'] as PoStatus[]).includes(po.status);
  const canAmend = (['DRAFT', 'APPROVED', 'SENT', 'ACKNOWLEDGED'] as PoStatus[]).includes(po.status);
  const canCapitalize = po.po_type === 'CAPEX';
  const canCreateServiceEntry = po.po_type === 'SERVICE';
  const noActionsAvailable = !canSubmit && !canApproveReject && !canSend && !canAcknowledge && !canCancel;

  const openAmend = () => {
    setAmendReason('');
    setAmendPriority(po.priority || '');
    setAmendExpectedDeliveryDate(po.expected_delivery_date ? po.expected_delivery_date.slice(0, 10) : '');
    setAmendPaymentTerms(po.payment_terms || '');
    setAmendNotes(po.terms_conditions || '');
    setAmendItems(
      po.items.map((it) => ({
        id: it.id,
        description: it.product_name || it.line_description || `Item ${it.id}`,
        quantity: String(toNum(it.quantity)),
        unitPrice: String(toNum(it.unit_price)),
        original: it,
      }))
    );
    setAmendOpen(true);
  };

  const openCapitalize = () => {
    setAssetName(po.po_number ? `${po.po_number} — Capital Asset` : '');
    setAssetCategoryId('');
    setCapitalizationDate(new Date().toISOString().slice(0, 10));
    setCapitalizeResult(null);
    setCapitalizeOpen(true);
    assetService
      .getCategories()
      .then((r) => setAssetCategories(r.categories || []))
      .catch(() => setAssetCategories([]));
  };

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmit = () =>
    runAction('submit', async () => {
      const res = await api.post<{ purchaseOrder: PurchaseOrderDetail; autoApproved: boolean }>(
        `/procurement/purchase-orders/${id}/submit`
      );
      setNotice(
        res.autoApproved
          ? 'No matching approval workflow -- purchase order was auto-approved.'
          : 'Purchase order submitted for approval.'
      );
      await load();
    });

  const handleApprove = () =>
    runAction('approve', async () => {
      await api.post(`/procurement/purchase-orders/${id}/approve`, { comment: approveComment.trim() || undefined });
      setApproveOpen(false);
      setApproveComment('');
      setNotice('Purchase order approved.');
      await load();
    });

  const handleReject = () =>
    runAction('reject', async () => {
      await api.post(`/procurement/purchase-orders/${id}/reject`, { reason: rejectReason.trim() || undefined });
      setRejectOpen(false);
      setRejectReason('');
      setNotice('Purchase order rejected.');
      await load();
    });

  const handleSend = () =>
    runAction('send', async () => {
      await api.post(`/procurement/purchase-orders/${id}/send`);
      setNotice('Purchase order marked as sent to vendor.');
      await load();
    });

  const handleAcknowledge = () =>
    runAction('acknowledge', async () => {
      await api.post(`/procurement/purchase-orders/${id}/acknowledge`);
      setNotice('Vendor acknowledgement recorded.');
      await load();
    });

  const handleCancel = () =>
    runAction('cancel', async () => {
      await api.post(`/procurement/purchase-orders/${id}/cancel`, { reason: cancelReason.trim() || undefined });
      setCancelOpen(false);
      setCancelReason('');
      setNotice('Purchase order cancelled.');
      await load();
    });

  const wasApprovedBeforeAmend = po.approval_status === 'APPROVED';

  const handleAmend = () =>
    runAction('amend', async () => {
      const payload: AmendPoInput = {
        reason: amendReason.trim(),
        expectedDeliveryDate: amendExpectedDeliveryDate || undefined,
        paymentTerms: amendPaymentTerms.trim() || undefined,
        notes: amendNotes.trim() || undefined,
        priority: amendPriority || undefined,
        items: amendItems.map((it) => ({
          productId: it.original.product_id,
          itemType: it.original.item_type || undefined,
          quantity: toNum(it.quantity),
          unitPrice: toNum(it.unitPrice),
          gstRate: it.original.gst_rate !== undefined ? toNum(it.original.gst_rate) : undefined,
          lineDescription: it.original.line_description || undefined,
          hsnCode: it.original.line_hsn_code || it.original.hsn_code || undefined,
          uom: it.original.line_uom || it.original.unit || undefined,
        })),
      };
      await procurementService.amendPurchaseOrder(id!, payload);
      setAmendOpen(false);
      setNotice(
        wasApprovedBeforeAmend
          ? 'Purchase order amended. Its approval was voided and it is back in DRAFT for re-approval.'
          : 'Purchase order amended.'
      );
      await load();
    });

  const handleCapitalize = () =>
    runAction('capitalize', async () => {
      const result = await procurementService.capitalizePo(id!, {
        assetName: assetName.trim(),
        assetCategoryId: assetCategoryId || undefined,
        capitalizationDate,
      });
      setCapitalizeResult(result);
      setNotice('Purchase order capitalized.');
      await load();
    });

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement')} sx={{ mb: 1 }}>
        Back to Procurement
      </Button>

      <PageHeader
        title={po.po_number}
        qualifier={po.vendor_name ? `· ${po.vendor_name}` : undefined}
        subtitle={`Raised ${fmtDate(po.po_date)}${po.created_by_name ? ` by ${po.created_by_name}` : ''}`}
        actions={
          <>
            <TypePill label={po.po_type?.replace(/_/g, ' ') || 'MATERIAL'} tint={TYPE_TINT[po.po_type] || '#475569'} />
            <Chip size="small" label={po.priority} color={PRIORITY_COLOR[po.priority] || 'default'} />
            <StatusDot label={po.status.replace(/_/g, ' ')} tone={STATUS_TONE[po.status] || 'neutral'} />
            <StatusDot
              label={`Approval: ${po.approval_status.replace(/_/g, ' ')}`}
              tone={APPROVAL_STATUS_TONE[po.approval_status] || 'neutral'}
            />
          </>
        }
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Workflow actions */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canSubmit && (
            <Button variant="contained" startIcon={<Send />} disabled={actionLoading !== null} onClick={handleSubmit}>
              {actionLoading === 'submit' ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          )}
          {canApproveReject && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                disabled={actionLoading !== null}
                onClick={() => setApproveOpen(true)}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<HighlightOff />}
                disabled={actionLoading !== null}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            </>
          )}
          {canSend && (
            <Button
              variant="contained"
              startIcon={<Handshake />}
              disabled={actionLoading !== null}
              onClick={handleSend}
            >
              {actionLoading === 'send' ? 'Sending...' : 'Send to Vendor'}
            </Button>
          )}
          {canAcknowledge && (
            <Button
              variant="contained"
              startIcon={<LocalShipping />}
              disabled={actionLoading !== null}
              onClick={handleAcknowledge}
            >
              {actionLoading === 'acknowledge' ? 'Recording...' : 'Acknowledge Vendor Receipt'}
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              disabled={actionLoading !== null}
              onClick={() => setCancelOpen(true)}
            >
              Cancel Purchase Order
            </Button>
          )}
          {canAmend && (
            <Button variant="outlined" startIcon={<EditNote />} disabled={actionLoading !== null} onClick={openAmend}>
              Amend
            </Button>
          )}
          {canCapitalize && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Business />}
              disabled={actionLoading !== null}
              onClick={openCapitalize}
            >
              Capitalize
            </Button>
          )}
          {canCreateServiceEntry && (
            <Button
              variant="outlined"
              startIcon={<ReceiptLong />}
              onClick={() => navigate(`/procurement/service-entries/new?poId=${id}`)}
            >
              Create Service Entry
            </Button>
          )}
          {noActionsAvailable && (
            <Typography variant="body2" color="text.secondary">
              This purchase order is {po.status.replace(/_/g, ' ').toLowerCase()} -- no further actions are
              available.
            </Typography>
          )}
        </Box>
      </Paper>

      <Grid container spacing={2.5}>
        {/* Header details */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Purchase Order Details
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['Vendor', po.vendor_name],
                ['Vendor GSTIN', po.vendor_gstin],
                ['Contact', po.contact_person],
                ['Cost Center', po.cost_center_name],
                ['Project', po.project_name],
                ['Department', po.department],
                ['PO Date', fmtDate(po.po_date)],
                ['Delivery Date', fmtDate(po.delivery_date)],
                ['Expected Delivery', fmtDate(po.expected_delivery_date)],
                ['Payment Terms', po.payment_terms],
                ['Approved By', po.approved_by_name],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6, sm: 4 }} key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Totals */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Totals
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatIndianNumber(toNum(po.subtotal))}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Tax</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatIndianNumber(toNum(po.tax_amount))}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatIndianNumber(toNum(po.total_amount))}</Typography>
            </Box>
            {po.terms_conditions && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Notes / Terms
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{po.terms_conditions}</Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Items */}
        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Line Items
              </Typography>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  {['Item', 'Type', 'HSN', 'UOM', 'Qty', 'Unit Price', 'Received', 'Invoiced', 'GST', 'Line Total'].map(
                    (h, i) => (
                      <TableCell key={h} align={i >= 4 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {po.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ border: 0 }}>
                      <TableEmptyState title="No line items on this purchase order" />
                    </TableCell>
                  </TableRow>
                ) : (
                  po.items.map((item) => (
                    <TableRow key={item.id} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.product_name || item.line_description || '—'}
                        </Typography>
                        {item.line_description && item.product_name && (
                          <Typography variant="caption" color="text.secondary">
                            {item.line_description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.item_type || '—'}</TableCell>
                      <TableCell>{item.line_hsn_code || item.hsn_code || '—'}</TableCell>
                      <TableCell>{item.line_uom || item.unit || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {toNum(item.quantity)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatIndianNumber(toNum(item.unit_price))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {toNum(item.received_quantity)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {toNum(item.invoiced_quantity)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {item.gst_rate !== undefined ? `${toNum(item.gst_rate)}%` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatIndianNumber(toNum(item.line_total))}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableSurface>
        </Grid>

        {/* Traceability rail */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: '100%' }}>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Traceability
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Requisition → Purchase Order → Goods Receipt → Invoice → Payment
              </Typography>
            </Box>

            <Box sx={{ px: 2.5, pb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Assignment fontSize="inherit" /> Source Requisition
              </Typography>
              {po.related.requisition ? (
                <Box
                  sx={{ mt: 0.5, mb: 1.5, cursor: 'pointer' }}
                  onClick={() => navigate(`/procurement/requisitions/${po.related.requisition!.id}`)}
                >
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                    {po.related.requisition.requisition_number}
                  </Typography>
                  <StatusDot label={po.related.requisition.status.replace(/_/g, ' ')} tone="neutral" />
                </Box>
              ) : (
                <Typography variant="body2" color="text.disabled" sx={{ mb: 1.5 }}>
                  Not raised from a requisition.
                </Typography>
              )}
            </Box>

            <Divider />

            <Box sx={{ px: 2.5, py: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <LocalShipping fontSize="inherit" /> Goods Receipts
              </Typography>
              {po.related.goodsReceipts.length === 0 ? (
                <Typography variant="body2" color="text.disabled">No goods have been received yet.</Typography>
              ) : (
                po.related.goodsReceipts.map((grn) => (
                  <Box
                    key={grn.id}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                    onClick={() => navigate('/procurement/goods-receipts')}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{grn.grn_number}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(grn.receipt_date)}{grn.warehouse_name ? ` · ${grn.warehouse_name}` : ''}
                      </Typography>
                    </Box>
                    <StatusDot label={grn.status.replace(/_/g, ' ')} tone={GRN_STATUS_TONE[grn.status] || 'neutral'} />
                  </Box>
                ))
              )}
            </Box>

            <Divider />

            <Box sx={{ px: 2.5, py: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <Receipt fontSize="inherit" /> Purchase Invoices
              </Typography>
              {po.related.invoices.length === 0 ? (
                <Typography variant="body2" color="text.disabled">No invoices booked yet.</Typography>
              ) : (
                po.related.invoices.map((inv) => (
                  <Box
                    key={inv.id}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                    onClick={() => navigate('/procurement/invoices')}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{inv.invoice_no}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(inv.invoice_date)} · {formatIndianNumber(toNum(inv.net_amount))}
                      </Typography>
                    </Box>
                    <StatusDot label={inv.status.replace(/_/g, ' ')} tone="neutral" />
                  </Box>
                ))
              )}
            </Box>

            <Divider />

            <Box sx={{ px: 2.5, py: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <Payments fontSize="inherit" /> Vendor Payments
              </Typography>
              {po.related.payments.length === 0 ? (
                <Typography variant="body2" color="text.disabled">No payments recorded yet.</Typography>
              ) : (
                po.related.payments.map((pay) => (
                  <Box
                    key={pay.id}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                    onClick={() => navigate('/procurement/payments')}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{pay.payment_number}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(pay.payment_date)} · {formatIndianNumber(toNum(pay.net_amount))}
                      </Typography>
                    </Box>
                    <StatusDot label={pay.status.replace(/_/g, ' ')} tone="neutral" />
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Approval history */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: '100%' }}>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <History fontSize="small" /> Approval History
              </Typography>
            </Box>
            {!po.approval ? (
              <Box sx={{ px: 2.5, pb: 2.5 }}>
                <Typography variant="body2" color="text.disabled">
                  No approval workflow has been triggered for this purchase order.
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ px: 2.5, pb: 1 }}>
                  <Typography variant="body2">
                    <strong>{po.approval.request.title}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Requested {fmtDateTime(po.approval.request.created_at)} · Step {po.approval.request.current_step}
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Step', 'Action', 'Comments', 'When'].map((h) => (
                        <TableCell key={h} sx={headerCellSx}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {po.approval.actions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ border: 0 }}>
                          <TableEmptyState title="No approval actions recorded yet" />
                        </TableCell>
                      </TableRow>
                    ) : (
                      po.approval.actions.map((a) => (
                        <TableRow key={a.id} sx={bodyRowSx}>
                          <TableCell sx={{ py: 1 }}>{a.step_number}</TableCell>
                          <TableCell>
                            <StatusDot label={ACTION_LABEL[a.action] || a.action} tone={ACTION_TONE[a.action] || 'neutral'} />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Typography variant="caption" color="text.secondary">
                              {a.comments || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {fmtDateTime(a.created_at)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </Paper>
        </Grid>

        {/* Amendment history */}
        <Grid size={{ xs: 12 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Layers fontSize="small" /> Amendment History
              </Typography>
            </Box>
            {(() => {
              const amendments = po.amendments ?? amendmentsFallback ?? [];
              if (amendments.length === 0) {
                return (
                  <Box sx={{ px: 2.5, pb: 2.5 }}>
                    <Typography variant="body2" color="text.disabled">
                      This purchase order has not been amended.
                    </Typography>
                  </Box>
                );
              }
              return (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Version', 'Reason', 'Changed By', 'When'].map((h) => (
                        <TableCell key={h} sx={headerCellSx}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {amendments.map((a) => (
                      <TableRow key={a.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1 }}>v{a.version}</TableCell>
                        <TableCell sx={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {a.reason}
                        </TableCell>
                        <TableCell>{a.changed_by_name || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {fmtDateTime(a.changed_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </Paper>
        </Grid>
      </Grid>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Purchase Order</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Comment (optional)"
            fullWidth
            multiline
            rows={3}
            value={approveComment}
            onChange={(e) => setApproveComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading === 'approve'}>
            {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Purchase Order</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            The reason will be visible in the approval history.
          </DialogContentText>
          <TextField
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading === 'reject'}>
            {actionLoading === 'reject' ? 'Rejecting...' : 'Reject Purchase Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Purchase Order</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            This cannot be undone. Any pending approval request will also be cancelled.
          </DialogContentText>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep It</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={actionLoading === 'cancel'}>
            {actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel Purchase Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Amend dialog */}
      <Dialog open={amendOpen} onClose={() => setAmendOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Amend Purchase Order</DialogTitle>
        <DialogContent dividers>
          {wasApprovedBeforeAmend && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This purchase order is currently approved. Amending it voids that approval -- it returns to DRAFT and
              must be re-approved.
            </Alert>
          )}
          <TextField
            label="Reason for Amendment *"
            fullWidth
            multiline
            rows={2}
            value={amendReason}
            onChange={(e) => setAmendReason(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Priority"
                value={amendPriority}
                onChange={(e) => setAmendPriority(e.target.value)}
              >
                {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Expected Delivery Date"
                value={amendExpectedDeliveryDate}
                onChange={(e) => setAmendExpectedDeliveryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Payment Terms"
                value={amendPaymentTerms}
                onChange={(e) => setAmendPaymentTerms(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Notes / Terms"
                value={amendNotes}
                onChange={(e) => setAmendNotes(e.target.value)}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Line Items
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Item</TableCell>
                <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                <TableCell sx={headerCellSx} align="right">Unit Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {amendItems.map((it) => (
                <TableRow key={it.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                    {it.description}
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 100 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.quantity}
                      onChange={(e) =>
                        setAmendItems((prev) =>
                          prev.map((p) => (p.id === it.id ? { ...p, quantity: e.target.value } : p))
                        )
                      }
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ minWidth: 90 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.unitPrice}
                      onChange={(e) =>
                        setAmendItems((prev) =>
                          prev.map((p) => (p.id === it.id ? { ...p, unitPrice: e.target.value } : p))
                        )
                      }
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ minWidth: 110 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAmendOpen(false)} disabled={actionLoading === 'amend'}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAmend}
            disabled={amendReason.trim().length < 3 || actionLoading === 'amend'}
          >
            {actionLoading === 'amend' ? 'Amending...' : 'Submit Amendment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Capitalize dialog */}
      <Dialog open={capitalizeOpen} onClose={() => (actionLoading === 'capitalize' ? undefined : setCapitalizeOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Capitalize Purchase Order</DialogTitle>
        <DialogContent dividers>
          {!capitalizeResult ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <DialogContentText>
                Creates a fixed asset from this CAPEX purchase order and posts the CWIP → Fixed Asset journal entry
                (DR 1000 · CR 1020).
              </DialogContentText>
              <TextField
                label="Asset Name *"
                fullWidth
                value={assetName}
                onChange={(e) => setAssetName(e.target.value)}
                autoFocus
              />
              <TextField
                select
                fullWidth
                label="Asset Category"
                value={assetCategoryId}
                onChange={(e) => setAssetCategoryId(e.target.value)}
                helperText={assetCategories.length === 0 ? 'No asset categories available -- leave blank' : undefined}
              >
                <MenuItem value="">Not assigned</MenuItem>
                {assetCategories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Capitalization Date"
                type="date"
                fullWidth
                value={capitalizationDate}
                onChange={(e) => setCapitalizationDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="success">Purchase order capitalized successfully.</Alert>
              <Box>
                <Typography variant="caption" color="text.secondary">Capitalised Amount</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formatIndianNumber(toNum(capitalizeResult.capitalisedAmount))}
                </Typography>
              </Box>
              {capitalizeResult.asset && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Fixed Asset Created</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {capitalizeResult.asset.asset_name || capitalizeResult.asset.name || `Asset #${capitalizeResult.asset.id}`}
                  </Typography>
                  {capitalizeResult.asset.asset_code && (
                    <Typography variant="caption" color="text.secondary">{capitalizeResult.asset.asset_code}</Typography>
                  )}
                </Box>
              )}
              {capitalizeResult.journalEntry && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Journal Entry (CWIP → Fixed Asset)</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {capitalizeResult.journalEntry.journal_number || capitalizeResult.journalEntry.entry_number || `JE #${capitalizeResult.journalEntry.id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">DR 1000 Fixed Assets · CR 1020 CWIP</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!capitalizeResult ? (
            <>
              <Button onClick={() => setCapitalizeOpen(false)} disabled={actionLoading === 'capitalize'}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCapitalize}
                disabled={!assetName.trim() || !capitalizationDate || actionLoading === 'capitalize'}
              >
                {actionLoading === 'capitalize' ? 'Capitalizing...' : 'Capitalize'}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setCapitalizeOpen(false)}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrderDetailPage;
